import React from 'react';
import { ClockIcon, EyeOffIcon, HourglassIcon, MessageSquareIcon, RotateCcwIcon, RouteIcon, ShuffleIcon, UsersIcon, XIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { arrivalLines, CostIcon, costs, finalCost, searchLines, sessionLines } from '../data/copy';
const costIcons: Record<CostIcon, typeof ClockIcon> = {
  clock: ClockIcon,
  hourglass: HourglassIcon,
  shuffle: ShuffleIcon,
  talk: MessageSquareIcon,
  crowd: UsersIcon,
  route: RouteIcon,
  eye: EyeOffIcon
};
export function ProblemSection() {
  return <section className="w-full bg-slate-900 py-10 [contain-intrinsic-size:auto_900px] [content-visibility:auto] sm:py-14 [.chalk_&]:bg-white" aria-labelledby="problem-heading" data-id="element-178" data-mp="35idgptukzu79">
      <div className="mx-auto max-w-6xl px-6" data-id="element-179" data-mp="3ehie2wuephhu">
        <SectionHeading eyebrow="The real problem" eyebrowTone="danger" title={<span id="problem-heading" data-id="element-181" data-mp="1p455gvf9h7ax">Does this sound familiar?</span>} data-id="element-180" data-mp="1p1spuowz7911" />

        <div className="mx-auto mt-8 grid max-w-5xl gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-182" data-mp="1k49aflojkq7i">
          <div data-id="element-183" data-mp="je6e2utcgc6t">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 [.chalk_&]:text-slate-600" data-id="element-184" data-mp="jeoiieq5twpt">
              Practice starts in
            </p>
            <p className="text-5xl font-extrabold leading-none tracking-tight text-orange-400 sm:text-7xl [.chalk_&]:text-orange-600" data-id="element-185" data-mp="sda0a7nt57pj">
              20 minutes.
            </p>
            <ul className="mt-6 space-y-1.5 text-lg text-slate-50/90 [.chalk_&]:text-slate-900/90" data-id="element-186" data-mp="123lcsilk4p81">
              {arrivalLines.map(line => <li key={line} data-id="element-187" data-mp="171wlur412vp7">{line}</li>)}
            </ul>
            <p className="mt-6 text-xl font-bold uppercase leading-tight tracking-tight text-slate-50 sm:text-2xl [.chalk_&]:text-slate-900" data-id="element-188" data-mp="174ivcwahbla2">
              And you are still trying to decide what to teach.
            </p>
            <p className="mt-5 font-semibold text-slate-50 [.chalk_&]:text-slate-900" data-id="element-189" data-mp="3dtfama60xlss">
              You spent part of the day:
            </p>
            <p className="mt-1 leading-relaxed text-slate-400 [.chalk_&]:text-slate-600" data-id="element-190" data-mp="3hv97s8h0g7o7">
              {searchLines.join(' ')}
            </p>
          </div>

          <div className="lg:border-l lg:border-slate-800 lg:pl-16 [.chalk_&]:lg:border-slate-200" data-id="element-191" data-mp="k1pczl0pnmjj">
            <p className="text-2xl font-bold uppercase leading-tight tracking-tight text-slate-50 sm:text-3xl [.chalk_&]:text-slate-900" data-id="element-192" data-mp="y1gnhbs0qkss">
              Then practice starts.
            </p>
            <ul className="mt-6 divide-y divide-slate-800 border-y border-slate-800 [.chalk_&]:divide-slate-200 [.chalk_&]:border-slate-200" data-id="element-193" data-mp="17yv3jaa157kk">
              {sessionLines.map(line => <li key={line} className="flex items-start gap-3 py-3.5 text-[1.02rem] text-slate-50/90 [.chalk_&]:text-slate-900/90" data-id="element-194" data-mp="12xxlcxp0sdj2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-400/15 [.chalk_&]:bg-red-600/15" aria-hidden="true" data-id="element-195" data-mp="1cpbgyuduot6x">
                    <XIcon className="h-3 w-3 text-red-400 [.chalk_&]:text-red-600" strokeWidth={3} data-id="element-196" data-mp="1qn9sls0sebiq" />
                  </span>
                  {line}
                </li>)}
            </ul>
            <p className="mt-6 text-slate-400 [.chalk_&]:text-slate-600" data-id="element-197" data-mp="25d4j5en8a0nh">And after all that preparation...</p>
            <p className="mt-1 text-2xl font-extrabold uppercase leading-tight tracking-tight text-slate-50 sm:text-3xl [.chalk_&]:text-slate-900" data-id="element-198" data-mp="2a9bksv8lcpzm">
              The practice still feels <span className="text-red-400 [.chalk_&]:text-red-600" data-id="element-199" data-mp="20bnhvi0u0c6d">improvised.</span>
            </p>
          </div>
        </div>

        <div className="mt-12 sm:mt-16" data-id="element-200" data-mp="1mk0ih9blenjd">
          <h3 className="mx-auto max-w-3xl text-center text-2xl font-extrabold uppercase leading-tight tracking-tight text-slate-50 sm:text-3xl [.chalk_&]:text-slate-900" data-id="element-201" data-mp="1rctri9e3gi19">
            The real cost of not having a clear system
          </h3>

          <ul className="mx-auto mt-8 grid max-w-4xl grid-cols-2 gap-2.5 sm:mt-10 sm:gap-4" data-id="element-202" data-mp="1hknxyv871sjy">
            {costs.map((cost, index) => {
            const Icon = costIcons[cost.icon];
            const isLastOdd = index === costs.length - 1 && costs.length % 2 === 1;
            return <li key={cost.title} className={`flex flex-col gap-2.5 rounded-2xl border p-3.5 sm:flex-row sm:items-center sm:gap-4 sm:rounded-3xl sm:p-5 ${isLastOdd ? 'col-span-2 sm:mx-auto sm:w-[calc(50%-0.5rem)]' : ''}`} style={{
              backgroundColor: `${cost.color}0d`,
              borderColor: `${cost.color}33`
            }} data-id="element-203" data-mp="10k1nkflu7tqw" data-mpi={index}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl" style={{
                backgroundColor: cost.color
              }} aria-hidden="true" data-id="element-204" data-mp="huvbhbfju0e2">
                    <Icon className="h-4 w-4 text-white sm:h-6 sm:w-6" strokeWidth={2} data-id="element-205" data-mp="7w5rnd43e5v6" />
                  </span>
                  <div className="min-w-0" data-id="element-206" data-mp="3vvi5qh3cxbk">
                    <p className="text-sm font-bold uppercase leading-[1.1] tracking-tight text-slate-50 sm:text-lg [.chalk_&]:text-slate-900" data-id="element-207" data-mp="3x6r04pe8v9j">
                      {cost.title}
                    </p>
                    <p className="mt-1 text-[0.8rem] leading-snug text-slate-400 sm:text-[0.9rem] [.chalk_&]:text-slate-600" data-id="element-208" data-mp="1y70oj2jkbnul">
                      {cost.detail.join(' ')}
                    </p>
                  </div>
                </li>;
          })}
          </ul>

          <div className="mx-auto mt-5 flex max-w-4xl flex-col items-center gap-3 rounded-3xl border-2 border-orange-400/40 bg-orange-400/[0.06] px-6 py-9 text-center sm:px-10 sm:py-12 [.chalk_&]:border-orange-600/40 [.chalk_&]:bg-orange-600/[0.06]" data-id="element-209" data-mp="3pxuhwdpyz1mo">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-400/15 [.chalk_&]:bg-orange-600/15" aria-hidden="true" data-id="element-210" data-mp="326yt7pgt8mjn">
              <RotateCcwIcon className="h-7 w-7 text-orange-400 [.chalk_&]:text-orange-600" strokeWidth={2} data-id="element-211" data-mp="3670nveo4ofpf" />
            </span>
            <p className="mt-2 text-xl font-extrabold uppercase leading-tight tracking-tight text-slate-50 sm:text-2xl [.chalk_&]:text-slate-900" data-id="element-212" data-mp="3b5ajfqdrlibk">
              {finalCost.title}
            </p>
            <p className="text-slate-400 [.chalk_&]:text-slate-600" data-id="element-213" data-mp="qjirma2rf881">{finalCost.lead}</p>
            <p className="mt-1 text-3xl font-extrabold leading-[1.1] tracking-tight text-orange-400 sm:text-4xl [.chalk_&]:text-orange-600" data-id="element-214" data-mp="2mirbuwgh9sr3">
              {finalCost.question}
            </p>
          </div>
        </div>
      </div>
    </section>;
}